import {
  Alert,
  Badge,
  Button,
  Divider,
  Flex,
  FlexItem,
  Label,
  PageSection,
  Skeleton
} from "@patternfly/react-core";

import type { DashboardOrder, DashboardPayload, Location, User, ViewKey } from "../../types";
import {
  actionLabel,
  canViewCloseChecks,
  firstName,
  formatNumber,
  greeting,
  issueCount,
  plainNextAction,
  taskLaneForRow
} from "../../display";

export type TodayStatusTone = "danger" | "success" | "warning" | "default";

export type TodayStatusTile = {
  label: string;
  value: number;
  tone: TodayStatusTone;
  view: ViewKey;
};

type TodayWorkspaceProps = {
  user: User;
  dashboard: DashboardPayload | null;
  loading: boolean;
  error: string;
  locations: Location[];
  selectedLocation: string;
  orders: DashboardOrder[];
  actionRows: DashboardOrder[];
  onNavigate: (view: ViewKey) => Promise<void>;
  onSelectedLocationChange: (value: string) => void;
  onOpenOrder: (id: string | number) => Promise<void>;
};

export function TodayWorkspace({
  user,
  dashboard,
  loading,
  error,
  locations,
  selectedLocation,
  orders,
  actionRows,
  onNavigate,
  onSelectedLocationChange,
  onOpenOrder
}: TodayWorkspaceProps) {
  const kpis = dashboard?.operational_kpis || {};
  const closeChecks = issueCount(kpis, dashboard?.finance_close || null);
  const baseStatusTiles: TodayStatusTile[] = [
    { label: "Items Needing Attention", value: kpis.attention_items || actionRows.length, tone: "danger", view: "dashboard" },
    { label: "Open Orders", value: kpis.open_orders || orders.length, tone: "success", view: "po_request" },
    { label: "Branch Contested", value: kpis.contested_orders || 0, tone: "default", view: "dashboard" },
    { label: "Quantities Unserved", value: kpis.unserved_quantity || 0, tone: "warning", view: "dashboard" },
    { label: "Close Checks", value: closeChecks, tone: "default", view: "report_daily_inventory" }
  ];
  const statusTiles = baseStatusTiles.filter(tile => tile.label !== "Close Checks" || (canViewCloseChecks(user) && Number(tile.value || 0) > 0));

  return (
    <>
      <PageSection className="pf-toj-hero" hasBodyWrapper={false}>
        <div className="pf-toj-hero-inner">
          <div className="pf-toj-hero-copy">
            <div className="pf-toj-accent-line" />
            <h1>{greeting()} {firstName(user)}<span className="pf-toj-wave" aria-hidden="true">&nbsp;👋</span></h1>
            <p>Current status, items needing attention, and tools for this role.</p>
          </div>
          <div className="pf-toj-hero-controls">
            <Label color="green" className="pf-toj-sync-label">Online - All changes saved</Label>
            <select
              className="pf-toj-location-select"
              value={selectedLocation}
              onChange={event => onSelectedLocationChange(event.currentTarget.value)}
              aria-label="Filter by location"
            >
              <option value="">All visible locations</option>
              {locations.map(location => (
                <option key={location.id} value={String(location.id)}>
                  {[location.code, location.name].filter(Boolean).join(" - ")}
                </option>
              ))}
            </select>
          </div>
        </div>
      </PageSection>
      <PageSection className="pf-toj-dashboard" hasBodyWrapper={false}>
        <div id="pf-today-view">
          {error && <Alert variant="danger" title={error} />}
          {loading && (
            <div className="pf-toj-skeleton-stack" aria-label="Loading Today">
              <Skeleton height="72px" screenreaderText="Loading status totals" />
              <Skeleton height="260px" screenreaderText="Loading items needing attention" />
            </div>
          )}
          {!loading && (
            <>
              <section className="pf-toj-status-strip" aria-label="Work status">
                {statusTiles.map(tile => (
                  <button
                    key={tile.label}
                    type="button"
                    className={`pf-toj-status-tile pf-toj-status-${tile.tone}`}
                    onClick={() => void onNavigate(tile.view)}
                  >
                    <span data-pf-audit-status-label={tile.label}>{tile.label}</span>
                    <strong>{formatNumber(tile.value)}</strong>
                  </button>
                ))}
              </section>
              <section className="pf-toj-action-card">
                  <Flex alignItems={{ default: "alignItemsCenter" }} justifyContent={{ default: "justifyContentSpaceBetween" }}>
                    <FlexItem>
                      <h2>Action Needed</h2>
                      <p>Items waiting for this user or role.</p>
                    </FlexItem>
                    <FlexItem>
                      <Badge isRead>{actionRows.length}</Badge>
                    </FlexItem>
                  </Flex>
                  <Divider />
                  <div className="pf-toj-lane-row">
                    {["Review Orders", "Fill Orders", "Receive Deliveries", "Review Delivery Issue"].map(label => {
                      const count = actionRows.filter(row => taskLaneForRow(row) === label).length;
                      return (
                        <Button key={label} variant={count ? "secondary" : "control"} isDisabled={!count} className="pf-toj-lane-button">
                          <span>{label}</span>
                          <Badge isRead>{count}</Badge>
                        </Button>
                      );
                    })}
                  </div>
                  <div className="pf-toj-worklist" aria-label="Items needing attention">
                    <div className="pf-toj-worklist-head">
                      <span data-pf-audit-worklist-header="Requester">Requester</span>
                      <span data-pf-audit-worklist-header="Current Status">Current Status</span>
                      <span data-pf-audit-worklist-header="Available Actions">Available Actions</span>
                    </div>
                    {actionRows.length ? actionRows.map(row => (
                      <button
                        key={String(row.id)}
                        type="button"
                        className="pf-toj-work-row"
                        onClick={() => void onOpenOrder(row.id)}
                      >
                        <span>
                          <strong>{row.po_number || row.document_number || ""}</strong>
                          <small>{[row.request_code, row.request_name].filter(Boolean).join(" - ")}</small>
                        </span>
                        <span>{plainNextAction(row)}</span>
                        <span>
                          <Label color="green">{actionLabel(row)}</Label>
                          <small>{row.status || "Open"}</small>
                        </span>
                      </button>
                    )) : (
                      <div className="pf-toj-empty">
                        <strong>Nothing needs action right now.</strong>
                        <span>New work for this role will appear here.</span>
                      </div>
                    )}
                  </div>
              </section>
            </>
          )}
        </div>
      </PageSection>
    </>
  );
}
